import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { isCriticalLabInterpretation, LAB_VALUE_ERRORS, parseLabNumeric } from '@wodi/domain';
import type { LabReportView, LabSampleWorkView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Checkbox,
  Notice,
  RadioGroup,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { Fragment, useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { labKeys, saveLabResults, submitLabReport } from '../api/lab.ts';
import { sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { draftStore } from '../patients/drafts.ts';
import { formatDateOnly } from '../patients/format.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { flagOf } from './format.ts';
import styles from './lab.module.css';

type Component = LabReportView['components'][number];
type Format = LabSampleWorkView['analytes'][number];

/** What is typed for one analyte. Kept as text until it is sent (values are never floats). */
export interface EntryValue {
  text: string;
  optionCode: string;
  notDone: boolean;
  reason: string;
}

interface Draft {
  revision: number;
  values: Record<string, EntryValue>;
  comment: string;
}

const BLANK: EntryValue = { text: '', optionCode: '', notDone: false, reason: '' };

/** A lab result draft on this device (plan P7): per user, facility and report; cleared like every draft. */
export function labDraftKey(userId: string, facilityId: string, reportId: string): string {
  return `lab-results:${userId}:${facilityId}:${reportId}`;
}

function initialValues(report: LabReportView): Record<string, EntryValue> {
  const values: Record<string, EntryValue> = {};
  for (const r of report.results) {
    const input = r.input;
    values[r.analyteId] = {
      ...BLANK,
      ...(input.kind === 'numeric' || input.kind === 'text' ? { text: input.text } : {}),
      ...(input.kind === 'coded' ? { optionCode: input.optionCode } : {}),
      ...(input.kind === 'not_done' ? { notDone: true, reason: input.reason } : {}),
    };
  }
  return values;
}

function isEmpty(value: EntryValue, component: Component): boolean {
  if (value.notDone) return value.reason.trim() === '';
  if (component.resultType === 'coded') return value.optionCode === '';
  return value.text.trim() === '';
}

function numericError(value: EntryValue, format: Format | undefined): string | undefined {
  if (value.notDone || value.text.trim() === '' || format === undefined) return undefined;
  const parsed = parseLabNumeric(value.text, {
    decimals: format.decimals ?? 0,
    allowsNegative: format.allowsNegative,
  });
  return parsed.ok ? undefined : LAB_VALUE_ERRORS[parsed.code];
}

function inputOf(value: EntryValue, component: Component) {
  if (value.notDone) return { kind: 'not_done' as const, reason: value.reason.trim() };
  if (component.resultType === 'numeric') return { kind: 'numeric' as const, text: value.text };
  if (component.resultType === 'coded')
    return { kind: 'coded' as const, optionCode: value.optionCode };
  return { kind: 'text' as const, text: value.text.trim() };
}

class NeedsConfirmation extends Error {}

/**
 * Result entry for one report (plan §8.4): each component with its field, the range for
 * this patient beside it (or "No range set"), the previous result, and the flag once saved.
 * Save keeps it part-finished; submit sends it for validation, and a critical value first
 * asks the technologist to confirm it with a reason (the interruptive tier, plan §6.1).
 */
export function ResultEntryForm({
  report,
  formats,
}: {
  report: LabReportView;
  formats: LabSampleWorkView['analytes'];
}) {
  // A culture the lab marked critical is confirmed at submission like a critical value
  // (lab QC and microbiology plan Q10).
  const cultureCritical = report.culture?.critical === true;
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const store = draftStore();
  const draftKey =
    session === null ? null : labDraftKey(session.user.id, session.facility.id, report.id);

  const [values, setValues] = useState<Record<string, EntryValue>>(() => initialValues(report));
  const [comment, setComment] = useState(report.comment ?? '');
  const [dirty, setDirty] = useState(false);
  const [restored, setRestored] = useState(false);
  const [loaded, setLoaded] = useState(!store.available);
  const [confirming, setConfirming] = useState<LabReportView['results']>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [cultureReason, setCultureReason] = useState('');
  const [confirmingCulture, setConfirmingCulture] = useState(false);
  const [tried, setTried] = useState(false);

  // A draft typed on this device survives a reload or a dropped connection, but only for
  // the revision it was typed against; anything saved since wins.
  useEffect(() => {
    if (draftKey === null || !store.available) return;
    let active = true;
    void store
      .get(draftKey)
      .then((saved) => {
        const draft = saved as Draft | undefined | null;
        if (active && draft?.revision === report.revision) {
          setValues(draft.values);
          setComment(draft.comment);
          setDirty(true);
          setRestored(true);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
    // Only when the report changes, not on every revision the form itself causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, store]);

  useEffect(() => {
    if (!loaded || !dirty || draftKey === null || !store.available) return;
    const draft: Draft = { revision: report.revision, values, comment };
    void store.set(draftKey, draft).catch(() => undefined);
  }, [values, comment, dirty, loaded, draftKey, store, report.revision]);

  const formatOf = (analyteId: string) => formats.find((f) => f.analyteId === analyteId);
  const valueOf = (analyteId: string) => values[analyteId] ?? BLANK;
  const change = (analyteId: string, patch: Partial<EntryValue>) => {
    setValues((v) => ({ ...v, [analyteId]: { ...(v[analyteId] ?? BLANK), ...patch } }));
    setDirty(true);
  };
  const invalid = report.components.some(
    (c) => c.resultType === 'numeric' && numericError(valueOf(c.analyteId), formatOf(c.analyteId)),
  );

  const clearDraft = async () => {
    if (draftKey !== null) await store.delete(draftKey).catch(() => undefined);
  };

  const doSave = async (): Promise<LabReportView> => {
    const saved = await saveLabResults(report.id, {
      expectedRevision: report.revision,
      results: report.components
        .filter((c) => !isEmpty(valueOf(c.analyteId), c))
        .map((c) => ({ analyteId: c.analyteId, input: inputOf(valueOf(c.analyteId), c) })),
      comment: comment.trim() === '' ? null : comment.trim(),
    });
    await clearDraft();
    setDirty(false);
    setRestored(false);
    return saved;
  };

  const save = useMutation({
    mutationFn: doSave,
    onSuccess: async () => {
      toasts.show({ title: 'Results saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      const current = dirty ? await doSave() : report;
      const critical = current.results.filter((r) => isCriticalLabInterpretation(r.interpretation));
      const unconfirmed = critical.filter(
        (r) => (reasons[r.analyteId] ?? '').trim().length < REASON_MIN_LENGTH,
      );
      const cultureUnconfirmed = cultureCritical && cultureReason.trim().length < REASON_MIN_LENGTH;
      if (unconfirmed.length > 0 || cultureUnconfirmed) {
        setConfirming(critical);
        setConfirmingCulture(cultureCritical);
        throw new NeedsConfirmation();
      }
      return submitLabReport(
        current.id,
        current.revision,
        critical.map((r) => ({
          analyteId: r.analyteId,
          reason: (reasons[r.analyteId] ?? '').trim(),
        })),
        cultureCritical ? cultureReason.trim() : null,
      );
    },
    onSuccess: async () => {
      toasts.show({ title: 'Sent for validation' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
    onSettled: async (_data, error) => {
      if (error instanceof NeedsConfirmation)
        await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });

  const goSave = () => {
    setTried(true);
    if (!invalid && online) save.mutate();
  };
  const goSubmit = () => {
    setTried(true);
    if (!invalid && online) submit.mutate();
  };
  useShortcut('mod+s', goSave, { allowInFields: true });
  useShortcut('mod+enter', goSubmit, { allowInFields: true });

  const submitError =
    submit.error === null || submit.error instanceof NeedsConfirmation
      ? null
      : submit.error instanceof ApiError &&
          submit.error.code === 'lab_critical_acknowledgement_required'
        ? 'Some results are in the critical range. Confirm each one below, then submit again.'
        : errorText(submit.error, "The results weren't sent for validation. Try again.");

  return (
    <form
      className={styles.section}
      aria-label={`${report.testName} results`}
      onSubmit={(event) => {
        event.preventDefault();
        goSave();
      }}
    >
      {restored ? (
        <Notice tone="info">
          Restored what was typed on this device. Save to keep it; nothing is sent until you do.
        </Notice>
      ) : null}
      {!online ? (
        <Notice tone="info">
          Offline. What you type is kept on this device and can be saved when the connection is
          back.
        </Notice>
      ) : null}
      {report.components.map((component, index) => {
        const showHeading =
          component.heading !== null && component.heading !== report.components[index - 1]?.heading;
        return (
          <Fragment key={component.analyteId}>
            {showHeading ? <h3 className={styles.entryHeading}>{component.heading}</h3> : null}
            <EntryRow
              component={component}
              value={valueOf(component.analyteId)}
              format={formatOf(component.analyteId)}
              report={report}
              tried={tried}
              onChange={(patch) => {
                change(component.analyteId, patch);
              }}
            />
          </Fragment>
        );
      })}
      <TextArea
        label="Comment on the report (optional)"
        description="Printed on the report."
        value={comment}
        onChange={(value) => {
          setComment(value);
          setDirty(true);
        }}
        rows={2}
      />
      {confirming.map((r) => (
        <AlertPanel
          key={r.analyteId}
          danger
          title={`${r.analyteName} ${r.display}${r.unit === null ? '' : ` ${r.unit.display}`} is in the critical range. Confirm the value before it is validated.`}
          reason={reasons[r.analyteId] ?? ''}
          onReasonChange={(value) => {
            setReasons((current) => ({ ...current, [r.analyteId]: value }));
          }}
          reasonLabel="Why you are sure of this value"
          {...((reasons[r.analyteId] ?? '').trim().length < REASON_MIN_LENGTH
            ? { reasonError: 'For example "Repeated, same result". At least 5 characters.' }
            : {})}
        />
      ))}
      {confirmingCulture ? (
        <AlertPanel
          danger
          title={`${report.testName}: the culture is marked critical. Confirm it before it is validated.`}
          reason={cultureReason}
          onReasonChange={setCultureReason}
          reasonLabel="Why you are sure of this culture"
          {...(cultureReason.trim().length < REASON_MIN_LENGTH
            ? {
                reasonError: 'For example "Confirmed on repeat Gram stain". At least 5 characters.',
              }
            : {})}
        />
      ) : null}
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The results weren't saved. Try again.")}
        </Notice>
      ) : null}
      {submitError === null ? null : <Notice tone="error">{submitError}</Notice>}
      <div className={styles.actions}>
        <Button
          type="submit"
          isPending={save.isPending}
          pendingLabel="Saving…"
          isDisabled={!online}
          aria-keyshortcuts="Control+S"
        >
          Save
        </Button>
        <Button
          variant="primary"
          onPress={goSubmit}
          isPending={submit.isPending}
          pendingLabel="Sending…"
          isDisabled={!online}
          aria-keyshortcuts="Control+Enter"
        >
          {confirming.length > 0 || confirmingCulture
            ? 'Confirm and submit for validation'
            : 'Submit for validation'}
        </Button>
        <span className={styles.meta}>Ctrl+S saves. Ctrl+Enter submits.</span>
      </div>
    </form>
  );
}

function EntryRow({
  component,
  value,
  format,
  report,
  tried,
  onChange,
}: {
  component: Component;
  value: EntryValue;
  format: Format | undefined;
  report: LabReportView;
  tried: boolean;
  onChange: (patch: Partial<EntryValue>) => void;
}) {
  const saved = report.results.find((r) => r.analyteId === component.analyteId);
  const previous = report.previous.find((p) => p.analyteId === component.analyteId);
  const label = `${component.analyteName}${component.required ? '' : ' (optional)'}`;
  const error = component.resultType === 'numeric' ? numericError(value, format) : undefined;
  // The flag shown is the server's, for the value it judged; it clears while being retyped.
  const savedMatches =
    saved !== undefined &&
    !value.notDone &&
    ((saved.input.kind === 'numeric' && saved.input.text === value.text.trim()) ||
      (saved.input.kind === 'coded' && saved.input.optionCode === value.optionCode));
  const flag = savedMatches ? flagOf(saved.interpretation) : null;
  const options = component.options.filter(
    (o) => o.status === 'active' || o.code === value.optionCode,
  );

  let field;
  if (value.notDone) {
    field = (
      <TextField
        label={`${component.analyteName}: why not done`}
        value={value.reason}
        onChange={(reason) => {
          onChange({ reason });
        }}
        {...(tried && value.reason.trim() === ''
          ? { errorMessage: 'Say why the test was not done.' }
          : {})}
      />
    );
  } else if (component.resultType === 'numeric') {
    field = (
      <TextField
        label={label}
        inputMode="decimal"
        value={value.text}
        onChange={(text) => {
          onChange({ text });
        }}
        {...(component.unit === null ? {} : { unit: component.unit.display })}
        {...(error === undefined ? {} : { errorMessage: error })}
      />
    );
  } else if (component.resultType === 'coded') {
    field = (
      <RadioGroup
        label={label}
        value={value.optionCode}
        options={options.map((o) => ({ id: o.code, label: o.display }))}
        onChange={(optionCode) => {
          onChange({ optionCode });
        }}
      />
    );
  } else {
    field = (
      <TextArea
        label={label}
        value={value.text}
        onChange={(text) => {
          onChange({ text });
        }}
        rows={2}
      />
    );
  }

  const range = format?.range ?? null;
  return (
    <div className={styles.entry}>
      <div className={styles.stack}>
        {field}
        <Checkbox
          isSelected={value.notDone}
          onChange={(notDone) => {
            onChange({ notDone });
          }}
        >
          Not done
        </Checkbox>
      </div>
      <div className={styles.entryAside}>
        {component.resultType === 'numeric' ? (
          <span>
            {range === null
              ? 'No range set'
              : `Range ${range.text}${range.provisional ? ', provisional' : ''}`}
          </span>
        ) : null}
        <span>
          {previous === undefined
            ? 'No previous result'
            : `Previous ${previous.display} on ${formatDateOnly(previous.releasedAt)}`}
        </span>
        {savedMatches && saved.fromAnalyzer !== null ? (
          <span>{`From analyzer: ${saved.fromAnalyzer}`}</span>
        ) : null}
      </div>
      <div
        className={styles.flag}
        data-critical={flag?.critical === true || undefined}
        aria-live="polite"
      >
        {flag === null || flag.marker === '' ? null : (
          <>
            <span>{flag.marker}</span> <span className={styles.flagWord}>{flag.word}</span>
          </>
        )}
      </div>
    </div>
  );
}
